import { useAtom } from 'jotai'
import { useCallback, useContext, useMemo } from 'react'

import { bookingFromAtom, bookingStateAtom, bookingUntilAtom } from '../atoms/bookings'
import {
  dashboardRoomAtom,
  dashboardSearchUrlAtom,
  settingsSidebarAtom,
  showStatisticsUrlAtom,
} from '../atoms/dashboard'
import { ActiveTenantContext } from '../context/common/Context'
import { StatisticsConfig } from '@xeetr/types/models'

import { useUpdateUserSetting } from './requests/useUpdateUserSetting'
import { useUserSettings } from './useUserSettings'

export type { StatisticsConfig }

const defaultStatisticsConfig: StatisticsConfig = {
  showTotalBookings: true,
  showPeople: true,
  showTables: true,
  showTableAggregation: true,
  showCustomFields: true,
  enabledCustomFields: {},
}

export const useDashboard = () => {
  const { tenant } = useContext(ActiveTenantContext)
  const { settings: userSettings } = useUserSettings()
  const { update: updateStatistics } = useUpdateUserSetting(
    'statistics',
    'Fehler beim Speichern der Statistik-Einstellungen.',
    'Statistik-Einstellungen gespeichert.'
  )

  const [selectedRoom, setSelectedRoom] = useAtom(dashboardRoomAtom)
  const [selectedState, setSelectedState] = useAtom(bookingStateAtom)
  const [selectedFrom, setSelectedFrom] = useAtom(bookingFromAtom)
  const [selectedUntil, setSelectedUntil] = useAtom(bookingUntilAtom)
  const [filterText, setFilterText] = useAtom(dashboardSearchUrlAtom)
  const [showStatistics, setShowStatistics] = useAtom(showStatisticsUrlAtom)
  const [settingsSidebar, setSettingsSidebar] = useAtom(settingsSidebarAtom)

  // Get statistics config directly from React Query (single source of truth)
  const statisticsConfig = useMemo(() => {
    if (!tenant?.id) return defaultStatisticsConfig
    return userSettings.statistics?.[tenant.id] || defaultStatisticsConfig
  }, [tenant?.id, userSettings.statistics])

  const setStatisticsConfig = useCallback(
    (config: StatisticsConfig) => {
      updateStatistics(config)
    },
    [updateStatistics]
  )

  return {
    selectedRoom,
    setSelectedRoom,

    selectedState,
    setSelectedState,

    selectedFrom,
    setSelectedFrom,
    selectedUntil,
    setSelectedUntil,

    filterText,
    setFilterText,

    showStatistics,
    setShowStatistics,
    statisticsConfig,
    setStatisticsConfig,

    settingsSidebar,
    setSettingsSidebar,
  }
}
